import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, Save, Trash2 } from "lucide-react"
import { PageHeader } from "@/components/PageHeader"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { DraftEditor, type ReviewItem } from "@/components/generations/DraftEditor"
import { ShellFor } from "@/components/generations/ShellFor"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { PageProps } from "@/types/inertia"
import type { DraftCard, DraftQuestion, GenerationDetail } from "@/types/generations"

const POLL_MS = 2000

export default function GenerationShow({
  generation,
  content_area_groups,
}: {
  generation: GenerationDetail
  content_area_groups: ContentAreaGroup[]
}) {
  const intl = useIntl()
  const isDeck = generation.kind === "deck"
  const working = generation.status === "queued" || generation.status === "running"
  const title =
    generation.status === "drafted"
      ? isDeck ? intl.formatMessage({ id: "generations.review_title_deck" }) : intl.formatMessage({ id: "generations.review_title_quiz" })
      : isDeck ? intl.formatMessage({ id: "generations.working_title_deck" }) : intl.formatMessage({ id: "generations.working_title_quiz" })
  const description = isDeck
    ? intl.formatMessage({ id: "generations.show_meta_deck" }, { title: generation.source_title })
    : intl.formatMessage({ id: "generations.show_meta_quiz" }, { title: generation.source_title })
  const newHref = `/generations/new?kind=${generation.kind}${generation.official ? "&official=1" : ""}`

  // While drafting, reload just the generation prop until it's done.
  React.useEffect(() => {
    if (!working) return
    const timer = window.setInterval(() => router.reload({ only: ["generation"] }), POLL_MS)
    return () => window.clearInterval(timer)
  }, [working])

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <ShellFor official={generation.official}>
        <Link href={newHref} className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "generations.back_new" })}
        </Link>

        {working && <Progress generation={generation} />}
        {generation.status === "failed" && (
          <>
            <PageHeader
              title={intl.formatMessage({ id: "generations.failed_title" })}
              description={intl.formatMessage({ id: "generations.from_source" }, { title: generation.source_title })}
            />
            <div className="callout callout-danger mt-6" role="alert">
              <p>{generation.error_message || intl.formatMessage({ id: "generations.failed_fallback" })}</p>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button asChild>
                <Link href={`${newHref}&source_id=${generation.source_id}`}>{intl.formatMessage({ id: "generations.try_again" })}</Link>
              </Button>
              <Button type="button" variant="secondary" onClick={() => router.delete(`/generations/${generation.id}`)}>
                {intl.formatMessage({ id: "generations.dismiss" })}
              </Button>
            </div>
          </>
        )}
        {generation.status === "drafted" && (
          <Review key={generation.id} generation={generation} contentAreaGroups={content_area_groups} />
        )}
      </ShellFor>
    </>
  )
}

function Progress({ generation }: { generation: GenerationDetail }) {
  const intl = useIntl()
  const isDeck = generation.kind === "deck"
  const target = generation.requested_count
  const done = Math.min(generation.drafted_count, target)

  return (
    <>
      <PageHeader
        title={isDeck ? intl.formatMessage({ id: "generations.drafting_title_deck" }) : intl.formatMessage({ id: "generations.drafting_title_quiz" })}
        description={intl.formatMessage({ id: "generations.from_source" }, { title: generation.source_title })}
      />
      <div className="callout mt-8">
        <div
          className="h-1.5 overflow-hidden rounded-full bg-page"
          role="progressbar"
          aria-label={isDeck ? intl.formatMessage({ id: "generations.progress_cards" }) : intl.formatMessage({ id: "generations.progress_questions" })}
          aria-valuemin={0}
          aria-valuemax={target}
          aria-valuenow={done}
        >
          <div
            className="h-full bg-accent transition-[width] duration-500"
            style={{ width: `${Math.max(done / target, 0.04) * 100}%` }}
          />
        </div>
        <p className="mt-3" aria-live="polite">
          {generation.status === "queued"
            ? intl.formatMessage({ id: "generations.starting" })
            : done === 0
              ? isDeck ? intl.formatMessage({ id: "generations.planning_cards" }) : intl.formatMessage({ id: "generations.planning_questions" })
              : isDeck ? intl.formatMessage({ id: "generations.progress_text_cards" }, { done, target }) : intl.formatMessage({ id: "generations.progress_text_questions" }, { done, target })}
        </p>
        <p className="mt-1 text-sm text-ink-muted">
          {intl.formatMessage({ id: "generations.wait_note" })}
        </p>
      </div>
    </>
  )
}

type Item = ReviewItem<DraftCard> | ReviewItem<DraftQuestion>

function Review({
  generation,
  contentAreaGroups,
}: {
  generation: GenerationDetail
  contentAreaGroups: ContentAreaGroup[]
}) {
  const intl = useIntl()
  const { props } = usePage<PageProps>()
  const [title, setTitle] = React.useState(generation.title ?? "")
  const [description, setDescription] = React.useState(generation.description ?? "")
  const [items, setItems] = React.useState<Item[]>(() =>
    generation.items.map((item) => ({ ...item, removed: false }) as Item),
  )
  const [processing, setProcessing] = React.useState(false)
  const [discarding, setDiscarding] = React.useState(false)
  const kept = items.filter((item) => !item.removed)
  const isDeck = generation.kind === "deck"

  const save = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(
      `/generations/${generation.id}/approve`,
      { title, description, items: kept.map(({ removed: _removed, ...item }) => item) },
      { preserveScroll: true, onStart: () => setProcessing(true), onFinish: () => setProcessing(false) },
    )
  }

  return (
    <form onSubmit={save}>
      <PageHeader
        title={isDeck ? intl.formatMessage({ id: "generations.review_title_deck" }) : intl.formatMessage({ id: "generations.review_title_quiz" })}
        description={
          isDeck
            ? intl.formatMessage({ id: "generations.review_desc_deck" }, { count: items.length, title: generation.source_title })
            : intl.formatMessage({ id: "generations.review_desc_quiz" }, { count: items.length, title: generation.source_title })
        }
      />

      <div className="mt-8 space-y-4">
        <div className="space-y-2">
          <label htmlFor="draft-title">{intl.formatMessage({ id: "generations.draft_title" })}</label>
          <Input id="draft-title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="space-y-2">
          <label htmlFor="draft-description">{intl.formatMessage({ id: "generations.draft_description" })}</label>
          <textarea
            id="draft-description"
            className="form-control form-control-textarea min-h-16"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <section className="mt-10">
        <h2>{isDeck ? intl.formatMessage({ id: "generations.cards_heading" }) : intl.formatMessage({ id: "generations.questions_heading" })}</h2>
        <ol className="mt-4 space-y-3 p-0">
          {items.map((item, index) => (
            <DraftEditor
              key={index}
              index={index}
              item={item}
              contentAreaGroups={contentAreaGroups}
              onChange={(next) => setItems((all) => all.map((it, i) => (i === index ? next : it)))}
            />
          ))}
        </ol>
      </section>

      <div className="sticky bottom-0 -mx-1 mt-8 border-t border-hairline bg-page px-1 py-4">
        {props.errors?.items && <p className="mb-3 text-sm text-danger-display">{props.errors.items}</p>}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={processing || kept.length === 0}>
            <Save className="h-4 w-4" />{" "}
            {isDeck ? intl.formatMessage({ id: "generations.save_cards" }, { count: kept.length }) : intl.formatMessage({ id: "generations.save_questions" }, { count: kept.length })}
          </Button>
          <Button type="button" variant="secondary" onClick={() => setDiscarding(true)}>
            <Trash2 className="h-4 w-4" /> {intl.formatMessage({ id: "generations.discard" })}
          </Button>
          {generation.official && (
            <span className="text-sm text-ink-muted">{intl.formatMessage({ id: "generations.official_note" })}</span>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={discarding}
        onOpenChange={setDiscarding}
        title={intl.formatMessage({ id: "generations.discard_title" })}
        description={
          isDeck
            ? intl.formatMessage({ id: "generations.discard_body_cards" }, { count: items.length })
            : intl.formatMessage({ id: "generations.discard_body_questions" }, { count: items.length })
        }
        confirmLabel={intl.formatMessage({ id: "generations.discard" })}
        onConfirm={() => router.delete(`/generations/${generation.id}`)}
      />
    </form>
  )
}
